import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { WidgetSize } from '../../../types/Dashboard';
import { useAuth } from '../../../contexts/AuthContext';
import { firebaseService } from '../../../services/firebaseService';

interface BarChartWidgetProps {
  config: {
    chartType?: string;
    dateRange?: '7d' | '30d' | '90d' | '180d' | '365d' | 'custom';
    startDate?: string;
    endDate?: string;
  };
  size: WidgetSize;
}

const BarChartWidget: React.FC<BarChartWidgetProps> = ({ config, size }) => {
  const { userProfile } = useAuth();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');

  useEffect(() => {
    loadChartData();
  }, [config.chartType, config.dateRange, config.startDate, config.endDate, userProfile?.empresaCnpj]);

  const loadChartData = async () => {
    if (!userProfile?.empresaCnpj || !config.chartType) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const charts = await firebaseService.getDashboardCharts(userProfile.empresaCnpj);
      const mensalidades = await firebaseService.getAllMensalidades(userProfile.empresaCnpj);

      switch (config.chartType) {
        case 'receita-mensal':
          setTitle('Receita Mensal');
          setData(charts.chartData || []);
          break;
        case 'mensalidades-pagas-vs-pendentes':
          setTitle('Mensalidades Pagas vs Pendentes');
          const now = new Date();
          const mesAtual = now.getMonth();
          const anoAtual = now.getFullYear();
          const mensalidadesMes = mensalidades.filter(m => {
            const dataVencimento = new Date(m.dataVencimento);
            return dataVencimento.getMonth() === mesAtual && 
                   dataVencimento.getFullYear() === anoAtual;
          });
          const pagas = mensalidadesMes.filter(m => m.dataPagamento && m.dataPagamento !== '').length;
          const pendentes = mensalidadesMes.filter(m => !m.dataPagamento || m.dataPagamento === '').length;
          setData([
            { mes: 'Este Mês', pagas, pendentes }
          ]);
          break;
        case 'avaliacoes-por-nivel':
          setTitle('Avaliações por Nível');
          // Implementar lógica de avaliações
          setData([]);
          break;
        case 'avaliacoes-por-professor':
          setTitle('Avaliações por Professor');
          // Implementar lógica de avaliações
          setData([]);
          break;
        default:
          setTitle('Gráfico de Barras');
          setData(charts.chartData || []);
      }
    } catch (error) {
      console.error('Erro ao carregar dados do gráfico:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-300"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      {title && (
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex-shrink-0">{title}</h3>
      )}
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="mes" stroke="#6b7280" fontSize={12} />
          <YAxis stroke="#6b7280" fontSize={12} />
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }}
          />
          <Legend />
          <Bar dataKey="receita" fill="#3b82f6" name="Receita (R$)" />
          <Bar dataKey="pagas" fill="#22c55e" name="Pagas" />
          <Bar dataKey="pendentes" fill="#ef4444" name="Pendentes" />
        </BarChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BarChartWidget;

